import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { useIsCompactMode } from '@frontend/responsiveness';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { SurfaceHeader } from '@frontend/surface-header';
import { PopoverDialog, usePopoverDialog, useModalControl, SpinningLoader, Button } from '@frontend/design-system';
import { useTeamChatSelector } from '../../providers/team-chat.provider';
import { HeaderExpandCollapseIcon } from '../header-expand-collapse-icon/header-expand-collapse-icon';
import { UserStatusSettings } from '../user-status-settings/user-status-settings';
import { teamChatPopoverStyles, settingsButtonStyle } from './chat-title-bar.styles';

export const ChatTitleBar = () => {
  const t = useTranslation('team-chat').t;
  const { getTriggerProps, getDialogProps, close, isOpen } = usePopoverDialog({
    placement: 'bottom-start',
    initialOffset: { x: -12, y: 0 },
  });
  const { modalProps } = useModalControl();
  const { navigate: settingsNavigate } = useSettingsNavigate();

  const openDynamicConversation = useTeamChatSelector((ctx) => ctx.openDynamicConversation);
  const isCurrentUserStatusOpen = useTeamChatSelector((ctx) => ctx.isCurrentUserStatusOpen);
  const openUserStatus = useTeamChatSelector((ctx) => ctx.openUserStatus);
  const closeUserStatus = useTeamChatSelector((ctx) => ctx.closeUserStatus);
  const isNavExpanded = useTeamChatSelector((ctx) => ctx.isNavExpanded);

  const closeTeamChat = useTeamChatSelector((ctx) => ctx.closeTeamChat);
  const restart = useTeamChatSelector((ctx) => ctx.restart);
  const isDataLoading = useTeamChatSelector((ctx) => ctx.isDataLoading);
  const isCompactMode = useIsCompactMode();

  return (
    <section>
      <SurfaceHeader.Root>
        <SurfaceHeader.Row>
          <SurfaceHeader.Start>
            <HeaderExpandCollapseIcon />
            {isCompactMode && <SurfaceHeader.Icon iconName='caret-left-small' onClick={closeTeamChat} />}
          </SurfaceHeader.Start>
          <SurfaceHeader.InteractiveButton {...getTriggerProps()}>
            <SurfaceHeader.Title titlePostFixIcon={`caret-${isOpen ? 'up' : 'down'}-tiny`}>
              {t('Team Chat')}
            </SurfaceHeader.Title>
          </SurfaceHeader.InteractiveButton>
          <SurfaceHeader.Actions>
            <SurfaceHeader.Icon
              iconName='draft'
              trackingId='team-chat-2.0-start-new-conversation'
              label={t('New Message')}
              hoverLabel={t('New Message')}
              onClick={() => openDynamicConversation(undefined)}
            />
            {!isNavExpanded && !isCompactMode && (
              <>
                <SurfaceHeader.Divider />
                <SurfaceHeader.Icon
                  iconName='x'
                  onClick={closeTeamChat}
                  trackingId='team-chat-2.0-close-team-chat-button'
                />
              </>
            )}
          </SurfaceHeader.Actions>
        </SurfaceHeader.Row>
      </SurfaceHeader.Root>
      <PopoverDialog {...getDialogProps()} css={teamChatPopoverStyles}>
        <Button
          onClick={() => {
            close();
            openUserStatus();
          }}
          variant='secondary'
          css={settingsButtonStyle}
          trackingId='team-chat-2.0-open-my-status-modal'
        >
          {t('My Status')}
        </Button>
        <Button
          onClick={() => {
            close();
            settingsNavigate({ to: `/personal/notifications/:type`, params: { type: 'chat-message-new' } });
          }}
          variant='secondary'
          css={settingsButtonStyle}
          trackingId='team-chat-2.0-open-team-chat-settings'
        >
          {t('Team Chat Settings')}
        </Button>
        <Button
          variant='secondary'
          css={[
            settingsButtonStyle,
            css`
              display: flex;
              align-items: center;
              justify-content: space-between;
            `,
          ]}
          onClick={() => {
            close();
            restart();
          }}
          trackingId='team-chat-2.0-reload-team-chat'
        >
          <>
            <span>{t('Reload Team Chat')}</span>
            {isDataLoading && <SpinningLoader size={'xs'} />}
          </>
        </Button>
      </PopoverDialog>
      <UserStatusSettings
        modalProps={{ ...modalProps, show: isCurrentUserStatusOpen }}
        closeModal={() => closeUserStatus()}
      />
    </section>
  );
};
